import { useState, useEffect, useRef, useCallback } from "react";
import {
  createQuestionEntry,
  createAnswerEntry,
  formatUserNowTime,
} from "../utils/chatHelpers";
import { isValidUUID, isValidThreadId, getUserUuid } from "../utils/auth";
import {
  parseLinksIntoCitations,
  hasMarkdownLinks,
} from "../utils/citationParser";
import { getLastCaseConversations, createThread } from "../../../../api/chat";
import { BASE_URL } from "../../../../api/config";

const deleteThread = async (threadId) => {
  const user = localStorage.getItem("user");

  return fetch(BASE_URL + "chat/deleteThread", {
    method: "POST",
    headers: { "content-type": "application/json", "x-access-token": user },
    body: JSON.stringify({ thread_id: threadId }),
  });
};

const useChatStream = ({
  currentThreadId,
  setCurrentThreadId,
  createNewThread,
  ensureThreadExists,
  loadThreads,
  localThreadIds,
  speechMode,
  getReadyDocIds,
  clearAttachedFiles,
  threadType = "private",
  selectedModel = "",
  historyDepth = 5,
}) => {
  const [chatEntry, setChatEntry] = useState("");
  const [messagesByThreadId, setMessagesByThreadId] = useState({});
  const [streamsByThreadId, setStreamsByThreadId] = useState({});
  const [sentHistory, setSentHistory] = useState([]);
  const [isLoadingThread, setIsLoadingThread] = useState(false);
  const [threadLocked, setThreadLocked] = useState(false);
  const [threadLockedByName, setThreadLockedByName] = useState("");
  const [cancelDocumentId, setCancelDocumentId] = useState(null);

  const pendingMsgRef = useRef("");
  const streamContextRef = useRef(null);
  const controlBufRef = useRef("");
  const threadIdRef = useRef(currentThreadId);
  const localThreadIdsRef = useRef(localThreadIds);
  const sendPayloadRef = useRef(null);
  const loadFetchIdRef = useRef(null);
  const abortControllersRef = useRef({});
  const messagesByThreadIdRef = useRef(messagesByThreadId);
  const streamsByThreadIdRef = useRef(streamsByThreadId);

  const collaborativeStreamRef = useRef({
    documentId: null,
    threadId: null,
    text: "",
    agent: "",
  });

  useEffect(() => {
    threadIdRef.current = currentThreadId;
  }, [currentThreadId]);

  useEffect(() => {
    localThreadIdsRef.current = localThreadIds;
  }, [localThreadIds]);

  useEffect(() => {
    messagesByThreadIdRef.current = messagesByThreadId;
  }, [messagesByThreadId]);

  useEffect(() => {
    streamsByThreadIdRef.current = streamsByThreadId;
  }, [streamsByThreadId]);

  const activeMessages = currentThreadId
    ? messagesByThreadId[currentThreadId] || []
    : [];

  const activeStream = currentThreadId
    ? streamsByThreadId[currentThreadId]
    : null;

  const messages = activeMessages;
  const showLoading = !!activeStream?.showLoading;
  const streamingText = activeStream?.text || "";
  const isStreaming = !!activeStream?.isStreaming;
  const streamingCitations = activeStream?.citations || [];
  const statusText = activeStream?.statusText || "";

  const setThreadMessages = useCallback((threadId, updater) => {
    if (!threadId) return;

    setMessagesByThreadId((prev) => {
      const current = prev[threadId] || [];
      const next =
        typeof updater === "function" ? updater(current) : updater || [];

      return {
        ...prev,
        [threadId]: next,
      };
    });
  }, []);

  const patchThreadStream = useCallback((threadId, patch) => {
    if (!threadId) return;

    setStreamsByThreadId((prev) => ({
      ...prev,
      [threadId]: {
        ...(prev[threadId] || {}),
        ...patch,
      },
    }));
  }, []);

  const clearThreadStream = useCallback((threadId) => {
    if (!threadId) return;

    setStreamsByThreadId((prev) => {
      const next = { ...prev };
      delete next[threadId];
      return next;
    });
  }, []);

  const updateEntry = useCallback(
    (entryId, patch) => {
      const tid = threadIdRef.current;
      if (!tid) return;

      setThreadMessages(tid, (prev) =>
        prev.map((message) =>
          message.id === entryId ? { ...message, ...patch } : message,
        ),
      );
    },
    [setThreadMessages],
  );

  const handleToggleLike = useCallback(
    (entry) => {
      const newLike = !entry.like;
      updateEntry(entry.id, { like: newLike, dislike: false });
    },
    [updateEntry],
  );

  const handleToggleDislike = useCallback(
    (entry) => {
      const newDislike = !entry.dislike;
      updateEntry(entry.id, { dislike: newDislike, like: false });
    },
    [updateEntry],
  );

  const handleToggleCatalog = useCallback(
    (entry) => {
      updateEntry(entry.id, { catalog: !entry.catalog });
    },
    [updateEntry],
  );

  const handleStop = useCallback(() => {
    const ctx = streamContextRef.current;
    const tid = ctx?.threadId || threadIdRef.current;

    if (tid && abortControllersRef.current[tid]) {
      abortControllersRef.current[tid].abort();
      delete abortControllersRef.current[tid];
    }

    const msg = pendingMsgRef.current;
    if (msg) {
      setChatEntry(msg);
      pendingMsgRef.current = "";
    }

    streamContextRef.current = null;

    if (ctx?.isFirstMessage && ctx?.threadId) {
      deleteThread(ctx.threadId)
        .then(() => loadThreads())
        .catch((error) => console.error("deleteThread failed:", error));

      if (threadIdRef.current === ctx.threadId) {
        setCurrentThreadId(null);
      }
    }

    if (tid) {
      setThreadMessages(tid, (prev) => {
        const lastQuestionIndex = prev
          .map((message) => message.type)
          .lastIndexOf("question");

        if (lastQuestionIndex === -1) return prev;
        return prev.slice(0, lastQuestionIndex);
      });

      clearThreadStream(tid);
    }
  }, [clearThreadStream, loadThreads, setCurrentThreadId, setThreadMessages]);

  const handleRetry = useCallback(
    (entry) => {
      if (!entry.retryPayload) return;

      const {
        message,
        agent: retryAgent,
        threadId: retryThreadId,
        researchMode: retryResearchMode,
      } = entry.retryPayload;

      if (retryThreadId) {
        setThreadMessages(retryThreadId, (prev) =>
          prev.filter((messageEntry) => messageEntry.id !== entry.id),
        );
      }

      sendPayloadRef.current?.(
        message,
        retryAgent,
        retryThreadId,
        retryResearchMode,
      );
    },
    [setThreadMessages],
  );

  const sendPayload = async (message, agent, maybeThreadId, researchMode) => {
    const msg = message?.trim();
    if (!msg) return;

    let tid = isValidThreadId(maybeThreadId)
      ? maybeThreadId
      : isValidThreadId(threadIdRef.current)
        ? threadIdRef.current
        : null;

    if (!tid) {
      tid = createNewThread();
      threadIdRef.current = tid;
    }

    const ownerThreadId = tid;
    const ownerMessages = messagesByThreadIdRef.current[ownerThreadId] || [];
    const threadAge = ownerMessages.length <= 1 ? "new" : "old";

    streamContextRef.current = {
      threadId: ownerThreadId,
      isFirstMessage: threadAge === "new",
    };

    if (abortControllersRef.current[ownerThreadId]) {
      abortControllersRef.current[ownerThreadId].abort();
    }

    const abortController = new AbortController();
    abortControllersRef.current[ownerThreadId] = abortController;

    try {
      pendingMsgRef.current = msg;
      setChatEntry("");
      setSentHistory((prev) => [msg, ...prev].slice(0, 50));

      setThreadMessages(ownerThreadId, (prev) => [
        ...prev,
        createQuestionEntry(msg),
      ]);

      window.dispatchEvent(
        new CustomEvent("chat:draft-thread-submitted", {
          detail: {
            threadId: ownerThreadId,
            thread_type: threadType,
          },
        }),
      );

      patchThreadStream(ownerThreadId, {
        showLoading: true,
        isStreaming: false,
        text: "",
        citations: [],
        statusText: "",
      });

      await ensureThreadExists(ownerThreadId, msg, threadAge);

      const user = localStorage.getItem("user");
      const isPublic = threadType === "public";

      let fetchUrl;
      let fetchBody;

      if (isPublic) {
        fetchUrl = BASE_URL + "chat/postPublicChatMsg";
        fetchBody = {
          question: msg,
          chatThread: ownerThreadId,
          model: selectedModel,
          history_depth: historyDepth,
        };
      } else {
        const docIdsAttached = getReadyDocIds();

        fetchUrl = BASE_URL + "chat/postNewChatMsg";
        fetchBody = {
          question: msg,
          chatThread: ownerThreadId,
          LLM: agent,
          ...(researchMode ? { research_mode: researchMode } : {}),
          doc_ids: docIdsAttached.join(","),
          daysAgo: "365",
          domain: "All Domains",
          audio: speechMode,
        };
      }

      const response = await fetch(fetchUrl, {
        method: "POST",
        headers: {
          "content-type": "application/json",
          "x-access-token": user,
        },
        body: JSON.stringify(fetchBody),
        signal: abortController.signal,
      });

      if (!isPublic) clearAttachedFiles();

      if (!response.ok || !response.body) {
        throw new Error(`HTTP error: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      let output = "";
      let docIds = [];
      let documentId = "";
      let sources = [];
      let answerType = "";
      let citations = [];
      let relatedSources = [];
      let streamError = "";

      controlBufRef.current = "";

      const appendText = (text) => {
        if (!text) return;

        output += text;

        let nextText = output;
        let nextCitations = citations;

        if (isPublic && output.includes("](http")) {
          const parsed = parseLinksIntoCitations(output);
          nextText = parsed.cleanedText;

          if (parsed.citations.length > 0) {
            nextCitations = parsed.citations;
          }
        }

        patchThreadStream(ownerThreadId, {
          showLoading: false,
          statusText: "",
          isStreaming: true,
          text: nextText,
          citations: nextCitations,
        });
      };

      const tryConsumeControlJson = (maybeJson) => {
        const trimmed = maybeJson.trim();

        const isControl =
          trimmed.startsWith('{"document_id"') ||
          trimmed.startsWith('{"sources"') ||
          trimmed.startsWith('{"status"') ||
          trimmed.startsWith('{"error"');

        if (!isControl) return { consumed: false };

        try {
          const obj = JSON.parse(trimmed);

          if (trimmed.startsWith('{"error"')) {
            streamError = obj.error || "Something went wrong.";

            patchThreadStream(ownerThreadId, {
              statusText: "",
              showLoading: false,
              isStreaming: false,
            });

            return { consumed: true };
          }

          if (trimmed.startsWith('{"status"')) {
            patchThreadStream(ownerThreadId, {
              statusText: obj.status || "",
            });

            return { consumed: true };
          }

          if (trimmed.startsWith('{"document_id"')) {
            let docidsString = obj.doc_ids || "";

            if (typeof docidsString === "string") {
              docidsString = docidsString.trim();

              if (docidsString.startsWith("[")) {
                try {
                  docIds = JSON.parse(docidsString.replace(/'/g, '"'));
                } catch {
                  docIds = [];
                }
              } else {
                docIds = docidsString
                  ? docidsString
                      .split(",")
                      .map((item) => item.trim())
                      .filter(Boolean)
                  : [];
              }
            } else if (Array.isArray(docidsString)) {
              docIds = docidsString;
            } else {
              docIds = [];
            }

            documentId = obj.document_id || "";
            answerType = obj.answer_type || "";
            citations = obj.citations || [];
            relatedSources = obj.related_sources || [];

            if (citations.length > 0) {
              patchThreadStream(ownerThreadId, {
                citations,
              });
            }
          } else if (trimmed.startsWith('{"sources"')) {
            const src = obj.sources;

            sources = src
              ? String(src)
                  .split(",")
                  .map((item) => item.trim())
                  .filter(Boolean)
              : [];
          }

          return { consumed: true };
        } catch {
          return { consumed: false, needsBuffer: true };
        }
      };

      const isControlStart = (value) =>
        value.startsWith('{"document_id"') ||
        value.startsWith('{"sources"') ||
        value.startsWith('{"status"') ||
        value.startsWith('{"error"');

      const processChunk = (raw) => {
        if (!raw.includes('{"')) {
          appendText(raw);
          return;
        }

        const lines = raw.split("\n");
        let textAccum = "";

        for (let i = 0; i < lines.length; i += 1) {
          const trimmed = lines[i].trim();

          if (trimmed && isControlStart(trimmed)) {
            if (textAccum) {
              appendText(textAccum);
              textAccum = "";
            }

            const result = tryConsumeControlJson(trimmed);

            if (!result.consumed) {
              controlBufRef.current = trimmed;
            }
          } else {
            if (textAccum.length > 0) textAccum += "\n";
            textAccum += lines[i];
          }
        }

        if (textAccum) {
          appendText(textAccum);
        }
      };

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });

        if (controlBufRef.current) {
          controlBufRef.current += chunk;

          if (controlBufRef.current.includes("\n")) {
            const buffered = controlBufRef.current;
            controlBufRef.current = "";
            processChunk(buffered);
          }

          continue;
        }

        processChunk(chunk);
      }

      const tail = decoder.decode();

      if (tail || controlBufRef.current) {
        const final = (controlBufRef.current || "") + (tail || "");
        controlBufRef.current = "";
        processChunk(final);
      }

      if (streamError) {
        setThreadMessages(ownerThreadId, (prev) => [
          ...prev,
          createAnswerEntry(streamError, agent, {
            isError: true,
            retryPayload: {
              message: msg,
              agent,
              threadId: ownerThreadId,
              researchMode,
            },
          }),
        ]);
      } else if (!output.trim()) {
        setThreadMessages(ownerThreadId, (prev) => [
          ...prev,
          createAnswerEntry(
            "No response received. The model may be unavailable.",
            agent,
            {
              isError: true,
              retryPayload: {
                message: msg,
                agent,
                threadId: ownerThreadId,
                researchMode,
              },
            },
          ),
        ]);
      } else {
        let finalOutput = output.trimEnd();
        let finalCitations = citations;

        if (isPublic && hasMarkdownLinks(finalOutput)) {
          const parsed = parseLinksIntoCitations(finalOutput);
          finalOutput = parsed.cleanedText;
          finalCitations = parsed.citations;
        }

        setThreadMessages(ownerThreadId, (prev) => [
          ...prev,
          createAnswerEntry(finalOutput, agent, {
            docIds,
            sources,
            documentId,
            answerType,
            citations: finalCitations,
            relatedSources,
            freshStream: true,
          }),
        ]);
      }

      if (ownerThreadId && threadAge !== "new") {
        createThread(ownerThreadId, "", "old", formatUserNowTime(), threadType)
          .then(() => loadThreads())
          .catch((error) => console.error("last_chat update failed:", error));
      }
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("sendPayload error:", error);

        setThreadMessages(ownerThreadId, (prev) => [
          ...prev,
          createAnswerEntry("Something went wrong. Please try again.", agent, {
            isError: true,
            retryPayload: {
              message: msg,
              agent,
              threadId: ownerThreadId,
              researchMode,
            },
          }),
        ]);
      }
    } finally {
      pendingMsgRef.current = "";
      streamContextRef.current = null;
      delete abortControllersRef.current[ownerThreadId];
      clearThreadStream(ownerThreadId);
    }
  };

  sendPayloadRef.current = sendPayload;

  useEffect(() => {
    const myUuid = getUserUuid();

    const handleStreamStart = (event) => {
      const data = event.detail;
      if (data.sender_uuid === myUuid) return;

      const tid = data.thread_id;
      if (!tid) return;

      collaborativeStreamRef.current = {
        documentId: data.document_id,
        threadId: tid,
        text: "",
        agent: data.agent || "",
      };

      if (tid === threadIdRef.current) {
        setThreadLocked(true);
        setThreadLockedByName(data.sender_name || "Someone");
        setCancelDocumentId(data.document_id);
      }

      setThreadMessages(tid, (prev) => [
        ...prev,
        createQuestionEntry(data.question),
      ]);

      patchThreadStream(tid, {
        showLoading: false,
        isStreaming: true,
        text: "",
        citations: [],
        statusText: "",
      });
    };

    const handleStreamChunk = (event) => {
      const data = event.detail;

      if (data.document_id !== collaborativeStreamRef.current.documentId) {
        return;
      }

      const tid = collaborativeStreamRef.current.threadId;
      collaborativeStreamRef.current.text += data.token;

      patchThreadStream(tid, {
        showLoading: false,
        isStreaming: true,
        text: collaborativeStreamRef.current.text,
      });
    };

    const handleStreamEnd = (event) => {
      const data = event.detail;

      if (data.document_id !== collaborativeStreamRef.current.documentId) {
        return;
      }

      const { agent, threadId: tid } = collaborativeStreamRef.current;

      setThreadMessages(tid, (prev) => [
        ...prev,
        createAnswerEntry(data.message, agent, {
          documentId: data.document_id,
          answerType: data.answer_type || "text",
          citations: data.citations || [],
          relatedSources: data.citations || [],
        }),
      ]);

      clearThreadStream(tid);

      if (tid === threadIdRef.current) {
        setThreadLocked(false);
        setThreadLockedByName("");
        setCancelDocumentId(null);
      }

      collaborativeStreamRef.current = {
        documentId: null,
        threadId: null,
        text: "",
        agent: "",
      };
    };

    const handleStreamCancelled = (event) => {
      const data = event.detail;

      if (data.document_id !== collaborativeStreamRef.current.documentId) {
        return;
      }

      const tid = collaborativeStreamRef.current.threadId;
      clearThreadStream(tid);

      if (tid === threadIdRef.current) {
        setThreadLocked(false);
        setThreadLockedByName("");
        setCancelDocumentId(null);
      }

      collaborativeStreamRef.current = {
        documentId: null,
        threadId: null,
        text: "",
        agent: "",
      };
    };

    window.addEventListener("chat:stream-start", handleStreamStart);
    window.addEventListener("chat:stream-chunk", handleStreamChunk);
    window.addEventListener("chat:stream-end", handleStreamEnd);
    window.addEventListener("chat:stream-cancelled", handleStreamCancelled);

    return () => {
      window.removeEventListener("chat:stream-start", handleStreamStart);
      window.removeEventListener("chat:stream-chunk", handleStreamChunk);
      window.removeEventListener("chat:stream-end", handleStreamEnd);
      window.removeEventListener(
        "chat:stream-cancelled",
        handleStreamCancelled,
      );
    };
  }, [clearThreadStream, patchThreadStream, setThreadMessages]);

  useEffect(() => {
    const onDiscardDraft = (event) => {
      const { threadId } = event.detail || {};
      if (!threadId) return;

      const hasMessages =
        (messagesByThreadIdRef.current[threadId] || []).length > 0;
      const hasStream = !!streamsByThreadIdRef.current[threadId];

      if (hasMessages || hasStream) return;

      setMessagesByThreadId((prev) => {
        const next = { ...prev };
        delete next[threadId];
        return next;
      });

      setStreamsByThreadId((prev) => {
        const next = { ...prev };
        delete next[threadId];
        return next;
      });
    };

    window.addEventListener("chat:discard-draft-thread", onDiscardDraft);

    return () => {
      window.removeEventListener("chat:discard-draft-thread", onDiscardDraft);
    };
  }, []);

  useEffect(() => {
    if (!currentThreadId) {
      setIsLoadingThread(false);
      loadFetchIdRef.current = null;
      return;
    }

    if (!isValidUUID(currentThreadId)) {
      setIsLoadingThread(false);
      loadFetchIdRef.current = null;
      return;
    }

    if ((localThreadIdsRef.current || []).includes(currentThreadId)) {
      setIsLoadingThread(false);
      loadFetchIdRef.current = null;
      return;
    }

    const activeStreamForThread = streamsByThreadIdRef.current[currentThreadId];
    const hasCachedMessages =
      (messagesByThreadIdRef.current[currentThreadId] || []).length > 0;

    if (activeStreamForThread?.isStreaming || hasCachedMessages) {
      setIsLoadingThread(false);
      return;
    }

    const myThreadId = currentThreadId;
    loadFetchIdRef.current = myThreadId;
    setIsLoadingThread(true);

    getLastCaseConversations(myThreadId)
      .then((response) => {
        if (loadFetchIdRef.current !== myThreadId) return;

        const loaded = [];

        response.data.forEach((item) => {
          const urlDivs = item.sources
            ? item.sources
                .split(",")
                .map((url) => url.trim())
                .filter((url) => url !== "" && url.length >= 5)
            : [];

          loaded.push(createQuestionEntry(item.question));
          loaded.push(
            createAnswerEntry(item.answer, item.agent, {
              docIds: item.doc_ids,
              sources: urlDivs,
              documentId: item.document_id,
              citations: item.citations || [],
              relatedSources: item.related_sources || [],
              like: item.like,
              dislike: item.dislike,
              catalog: item.catalog,
            }),
          );
        });

        setThreadMessages(myThreadId, loaded);
        setIsLoadingThread(false);

        const lastItem = response.data[response.data.length - 1];

        if (lastItem?.streaming) {
          const myUuid = getUserUuid();

          collaborativeStreamRef.current = {
            documentId: lastItem.document_id,
            threadId: myThreadId,
            text: lastItem.answer || "",
            agent: lastItem.agent || "",
          };

          patchThreadStream(myThreadId, {
            showLoading: false,
            isStreaming: true,
            text: lastItem.answer || "",
            citations: lastItem.citations || [],
            statusText: "",
          });

          if (lastItem.streaming_by && lastItem.streaming_by !== myUuid) {
            setThreadLocked(true);
            setThreadLockedByName(lastItem.streaming_by_name || "Someone");
            setCancelDocumentId(lastItem.document_id);
          }
        }
      })
      .catch((error) => {
        if (loadFetchIdRef.current !== myThreadId) return;
        console.error("Failed to load thread history:", error);
        setIsLoadingThread(false);
      });
  }, [currentThreadId, patchThreadStream, setThreadMessages]);

  return {
    chatEntry,
    setChatEntry,
    messages,
    setMessages: (updater) => {
      const tid = threadIdRef.current;
      if (!tid) return;
      setThreadMessages(tid, updater);
    },
    sentHistory,
    showLoading,
    streamingText,
    isStreaming,
    streamingCitations,
    statusText,
    isLoadingThread,
    sendPayload,
    handleStop,
    handleRetry,
    handleToggleLike,
    handleToggleDislike,
    handleToggleCatalog,
    updateEntry,
    threadLocked,
    setThreadLocked,
    threadLockedByName,
    cancelDocumentId,
  };
};

export default useChatStream;
